import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { create_role, get_all_permissions } from '../api/rolesApi';
import { toast } from 'react-toastify';
import { Form } from 'react-bootstrap';
import { AddAdminInterface, AddRolesInterface, PermissionsInterface } from '../../../modules/accounts/components/settings/SettingsModel';
import { LoadingProgressBar } from '../../projects/components/LoadingProgressBar';

const AddRole = () => {
    // useNavigate
    const navigate = useNavigate();
    const [getPermission, setGetPermission] = useState<PermissionsInterface[]>([]);
    // Use Effect
    const { data, isLoading: isPermissionsLoading } = useQuery(
        "permissions",
        get_all_permissions, { refetchOnWindowFocus: false }
    );
    useEffect(() => {
        if (data) {
            setGetPermission(data.data);
        }
    }, [data]);

    // Define types for roleCheckboxes and globalCheckboxes
    type RoleCheckboxes = Record<string, Record<string, boolean>>;
    type GlobalCheckboxes = Record<string, boolean>;
    // Initialize roleCheckboxes and globalCheckboxes based on your data
    const initialRoleCheckboxes: RoleCheckboxes =
        JSON.parse(localStorage.getItem("roleCheckboxes") || "{}") || {};
    const initialGlobalCheckboxes: GlobalCheckboxes =
        JSON.parse(localStorage.getItem("globalCheckboxes") || "{}") || {};
    // I hide this function, before API integration code is working but after this is not work. So I comment the code, now functionality is working fine!
    // ;['view', 'add', 'edit', 'delete', 'restore'].forEach((permission) => {
    //   initialGlobalCheckboxes[permission] = areAllRelatedCheckboxesChecked(
    //     permission,
    //     initialRoleCheckboxes
    //   )
    // })
    const [roleCheckboxes, setRoleCheckboxes] = useState<RoleCheckboxes>(
        initialRoleCheckboxes
    );
    const [globalCheckboxes, setGlobalCheckboxes] = useState<GlobalCheckboxes>(
        initialGlobalCheckboxes
    );
    // Function to check if all related individual checkboxes are checked
    function areAllRelatedCheckboxesChecked(
        permission: string,
        roleCheckboxes: { [x: string]: { [x: string]: unknown } }
    ) {
        return getPermission
            .filter((value: { [x: string]: any }) => {
                if (permission === "log") {
                    return value.log;
                }
                return value[permission.toLowerCase()];
            })
            .every(
                (value: { permission_key: string | number }) =>
                    roleCheckboxes[value.permission_key]?.[permission]
            );
    };
    // Handle individual role checkbox change
    const handleRoleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        role: string,
        permission: string
    ) => {
        const { checked } = event.target;
        const updatedRoleCheckboxes = { ...roleCheckboxes };
        updatedRoleCheckboxes[role] = {
            ...updatedRoleCheckboxes[role],
            [permission]: checked,
        };
        setRoleCheckboxes(updatedRoleCheckboxes);
        setGlobalCheckboxes((prevCheckboxes) => ({
            ...prevCheckboxes,
            [permission]: areAllRelatedCheckboxesChecked(
                permission,
                updatedRoleCheckboxes
            ),
        }));
    };
    // Handle global checkbox change
    const handleGlobalCheckboxChange = (permission: string) => {
        const checked = !globalCheckboxes[permission];
        const updatedRoleCheckboxes = { ...roleCheckboxes };
        getPermission.forEach((value) => {
            if (permission === "log") {
                if (value.log) {
                    updatedRoleCheckboxes[value.permission_key] = {
                        ...updatedRoleCheckboxes[value.permission_key],
                        [permission]: checked,
                    };
                }
            } else if (value[permission.toLowerCase()]) {
                updatedRoleCheckboxes[value.permission_key] = {
                    ...updatedRoleCheckboxes[value.permission_key],
                    [permission]: checked,
                };
            }
        });
        setRoleCheckboxes(updatedRoleCheckboxes);
        const allRelatedCheckboxesChecked = areAllRelatedCheckboxesChecked(
            permission,
            updatedRoleCheckboxes
        );
        setGlobalCheckboxes((prevCheckboxes) => ({
            ...prevCheckboxes,
            [permission]: allRelatedCheckboxesChecked,
        }));
    };
    // Update Roles
    const [roleName, setRoleName] = useState<any | null>("");
    const [roleNameError, setRoleNameError] = useState<any | null>("");
    const [status, setStatus] = useState<any | null>(1);
    const queryClient = useQueryClient();
    // Update Roles Function
    const handleUpdateRole = useMutation(
        (updatedRolesData:AddRolesInterface ) => {
            return create_role(updatedRolesData.name,updatedRolesData.status, updatedRolesData.permissions);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries("permissions");
            },
            onSuccess: () => {
                toast.success("Role Add Successfully", {
                    theme: "colored",
                });
                setRoleCheckboxes({});
                setGlobalCheckboxes({});
                setRoleName("");
                setStatus(1);
                localStorage.removeItem("roleCheckboxes");
                localStorage.removeItem("globalCheckboxes");
                navigate("/roles/roles-list");
            },
            onError: (error: any) => {
                toast.error(error.response.data.message, {
                    theme: "colored",
                });
            },
        }
    );
    const handleSubmit = () => {
        if (roleName.length === 0) {
            toast.error("The role name field is required.", {
                theme: "colored",
            });
            return;
        }
    
        const selectedPermissions: string[] = [];
        let checkboxesAreEmpty = true;
    
        getPermission.forEach((value: { permission_key: string; log?: string }) => {
            ["view", "create", "update", "delete"/*, "log"*/].forEach((permission) => {
                if (roleCheckboxes[value.permission_key]?.[permission]) {
                    let permissionKey: string;
                    
                    if (permission === "log" && value.log) {
                        // Use the log permission key directly from API
                        permissionKey = value.log;
                    } else {
                        permissionKey = `${value.permission_key}-${permission}`;

                        // Special case for userActivityLogs and roleActivityLogs
                        if (value.permission_key === "userActivityLogs") {
                            permissionKey = "user-activity-log";
                        } else if (value.permission_key === "roleActivityLogs") {
                            permissionKey = "role-activity-log";
                        }

                        // Replace 'admin' with 'user' dynamically
                        const transformedKeyPermission = permissionKey.startsWith("admin")
                            ? permissionKey.replace("admin", "user")
                            : permissionKey;

                        // Transform 'userapplication' to 'user-application'
                        permissionKey = transformedKeyPermission.includes("userapplication")
                            ? transformedKeyPermission.replace("userapplication", "user-application")
                            : transformedKeyPermission;
                    }

                    if (!selectedPermissions.includes(permissionKey)) {
                        selectedPermissions.push(permissionKey);
                    }
                    checkboxesAreEmpty = false;
                }
            });
        });
    
        if (checkboxesAreEmpty) {
            toast.error("At least one checkbox must be selected.", {
                theme: "colored",
            });
            return;
        }
    
        const roleData = {
            name: roleName,
            status: status,
            permissions: selectedPermissions,
        };
    
        handleUpdateRole.mutate(roleData);
    };    
    return (
        <>
            {/* Loading Progress Bar */}
            <LoadingProgressBar isLoading={isPermissionsLoading || handleUpdateRole.isLoading} />
            
            <div className="card card-custom">
            <div
                className='card-header border-0'
            >
                <div className='card-title'>
                    <h3 className='fw-bolder'>Add Role</h3>
                </div>
            </div>
            <div className="card-body">
                <div className='row '>
                    <label
                        htmlFor="exampleFormControlInput1"
                        className="required col-lg-4 col-form-label fw-bold fs-6"
                    >
                        Role Name
                    </label>
                    <div className='col-lg-8 fv-row'>
                        <Form.Control
                            className="form-control form-control-solid mx-5 full-width"
                            type="text"
                            value={roleName}
                            placeholder='Role Name'
                            onChange={(e) => {
                                if (e.target.value) {
                                    setRoleNameError('')
                                } else {
                                    setRoleNameError('Role Name is Required')
                                }
                                setRoleName(e.target.value)
                            }}
                        />
                        {roleNameError && !roleName &&(
                            <div className='text-danger mx-5'>{roleNameError}</div>
                        )}
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className='row mb-6'>
                    <label className='col-lg-4 col-form-label fw-bold fs-6'>
                        <span>Status</span>
                    </label>
                    <div className='col-lg-8 fv-row'>
                        <select
                            className='form-control form-control-solid mb-5 mx-5'
                            onChange={(e) => setStatus(e.target.value)}
                            value={status}
                        >
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                        </select>
                    </div>
                </div>
            </div>
            <div className="card-header">
                <div className="container">
                    <div className="row">
                        <div className="col-4">
                            <div className="p-3 mt-10">
                                <h4 className="card-title">Global Role & Permission</h4>
                            </div>
                        </div>
                        <div className="col-8">
                            <div className="p-3">
                                <table className="table">
                                    <tbody>
                                        <tr>
                                            {["View", "Create", "Update", "Delete"/*, "Log"*/].map((option) => (
                                                <td className="text-left fw-bold">
                                                    <label key={option}>{option}</label>
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            {["view", "create", "update", "delete"/*, "log"*/].map((option) => (
                                                <td className="table-cell-style">
                                                    <div className="form-check form-check-custom form-check-solid">
                                                        <label key={option}>
                                                            <input
                                                                className="form-check-input me-5"
                                                                type="checkbox"
                                                                name={option}
                                                                checked={globalCheckboxes[option]}
                                                                onChange={() =>
                                                                    handleGlobalCheckboxChange(option)
                                                                }
                                                            />
                                                        </label>
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className="role-permission full-width" >
                    <div className="container mt-2">
                        {isPermissionsLoading ? (
                            // Skeleton Loading for Permissions
                            <>
                                {[1, 2, 3, 4, 5].map((skeletonIndex) => (
                                    <div className="row mb-5" key={skeletonIndex}>
                                        <div className="col-4">
                                            <div className="p-3 mt-6">
                                                <div className="placeholder placeholder-lg bg-light w-150px"></div>
                                            </div>
                                        </div>
                                        <div className="col-8">
                                            <div className="p-3">
                                                <table className="table">
                                                    <tbody>
                                                        <tr>
                                                            {[1, 2, 3, 4, 5].map((cellIndex) => (
                                                                <td key={cellIndex} className="table-cell-style">
                                                                    <div className="placeholder placeholder-sm bg-light w-30px h-30px rounded"></div>
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            getPermission.map((value) => (
                            <div className="row" key={value.permission_key}>
                                <div className="col-4">
                                    <div className="p-3 mt-6">
                                        <h5>{value.title && value.title.charAt(0).toUpperCase() + value.title.slice(1)}</h5>
                                    </div>
                                </div>
                                <div className="col-8">
                                    <div className="p-3">
                                        <table className="table">
                                            <tbody>
                                                <tr>
                                                    {["view", "create", "update", "delete"/*, "log"*/].map(
                                                        (option) => (
                                                            <td key={option} className="table-cell-style">
                                                                {(option === "log" ? value.log : value[option]) ? (
                                                                    <div className="form-check form-check-custom form-check-solid">
                                                                        <label>
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                name={option}
                                                                                checked={
                                                                                    roleCheckboxes[
                                                                                    value.permission_key
                                                                                    ]?.[option]
                                                                                }
                                                                                onChange={(event) =>
                                                                                    handleRoleCheckboxChange(
                                                                                        event,
                                                                                        value.permission_key,
                                                                                        option
                                                                                    )
                                                                                }
                                                                            />
                                                                        </label>
                                                                    </div>
                                                                ) : (
                                                                    <div />
                                                                )}
                                                            </td>
                                                        )
                                                    )}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        ))
                        )}
                    </div>
                </div>
            </div>
            <div className="card-footer">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-end">
                        <button
                            data-toggle="tooltip" data-placement="bottom" title="Navigate to Roles List"
                            className='btn btn-light-dark mx-3'
                            onClick={() => navigate('/roles/roles-list')}
                        >
                            Cancel
                        </button>
                            <button className="btn btn-light-dark" onClick={handleSubmit}>
                                Add Role
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};
export default AddRole